import { AlertTriangle, FileText, FolderKanban, History, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { FieldRow } from "../FieldRow";
import { Empty, ErrorState } from "../States";
import { DOC_TYPE_LABELS, type Field } from "../../lib/api";
import { fieldLabel, fileName } from "../../lib/words";
import { TableScroll } from "../TableScroll";
import { ApiError } from "../../lib/http";
import { ExportButtons } from "./ExportButtons";
import { assetExports, overviewExport } from "./exports";
import { documentOpenPath } from "../../pages/documentViewFlow";

/** One field as the register keeps it (`RegisterField.to_json`). */
export type RegisterField = Omit<Field, "schema_version" | "edit"> & { readers_agreeing?: number };

/** A revision finding from `registry/revision.py`. */
export interface RevisionFinding {
  reason: "content_changed" | "revised" | "possible_revision" | string;
  source_path?: string | null;
  candidate_replacement?: string | null;
  old_revision?: string | null;
  new_revision?: string | null;
  affected_fields?: string[];
}

/** `GET /assets/{package}/{tag}`: the register entry, its revision flag and its open cases. */
export interface AssetView {
  tag: string;
  package: string;
  version: number;
  completeness: number;
  needs_review: string[];
  fields: RegisterField[];
  sources: Record<string, unknown>[];
  identifiers: Record<string, unknown>[];
  stale: boolean;
  possible_revision: boolean;
  revision_findings: RevisionFinding[];
  open_cases: string[];
}

/** The file a finding names. A finding with no path says "a source document", never a made-up name. */
const findingFile = (path: string | null | undefined) => (path ? fileName(path) : "a source document");

export function findingText(f: RevisionFinding): string {
  const file = findingFile(f.source_path);
  const other = findingFile(f.candidate_replacement);
  if (f.reason === "content_changed") return `${file} has changed since it was read.`;
  if (f.reason === "revised") return `${file} is revision ${f.old_revision}. ${other} is revision ${f.new_revision}.`;
  if (f.reason === "possible_revision") {
    return `${other} prints the same document number as ${file}. Its revision mark could not be compared `
      + `(${f.old_revision ?? "unreadable"} against ${f.new_revision ?? "unreadable"}).`;
  }
  return `${file}: ${f.reason.replace(/_/g, " ")}.`;
}

/** A cited file by name, linked to its document view. A finding with no path is not linked. */
function CitedFile({ pkg, path, page }: { pkg: string; path: string | null | undefined; page?: number | null }) {
  const to = path ? documentOpenPath({ path, pkg, page }) : null;
  return to ? <Link to={to} title="Open in the document view">{findingFile(path)}</Link> : <>{findingFile(path)}</>;
}

function FindingList({ pkg, findings }: { pkg: string; findings: RevisionFinding[] }) {
  return (
    <ul className="stub-list">
      {findings.map((f, i) => (
        <li key={i}>
          {findingText(f)}
          {(f.source_path || f.candidate_replacement) && (
            <> Open {f.source_path && <CitedFile pkg={pkg} path={f.source_path} />}
              {f.source_path && f.candidate_replacement && " or "}
              {f.candidate_replacement && <CitedFile pkg={pkg} path={f.candidate_replacement} />}.</>
          )}
          {f.affected_fields && f.affected_fields.length > 0 && (
            <> Values that rest on it: {f.affected_fields.map(fieldLabel).join(", ")}.</>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Stale or possibly revised. Reports only: re-reading costs paid pages, so a person decides. */
export function RevisionBanner({ asset }: { asset: AssetView }) {
  const stale = asset.revision_findings.filter((f) => f.reason !== "possible_revision");
  const possible = asset.revision_findings.filter((f) => f.reason === "possible_revision");
  return (
    <>
      {asset.stale && (
        <div className="state-box fail" role="alert" style={{ maxWidth: "none", marginBottom: 16 }}>
          <AlertTriangle aria-hidden="true" />
          <div>
            <div className="state-title">This entry may be out of date</div>
            <p className="muted">A document it was read from has changed or has a later revision in the package.</p>
            <FindingList pkg={asset.package} findings={stale} />
            <p className="muted">Nothing is re-read automatically. Decide whether to run the extraction again.</p>
          </div>
        </div>
      )}
      {asset.possible_revision && (
        <div className="state-box" role="status" style={{ maxWidth: "none", marginBottom: 16, borderColor: "var(--color-review)" }}>
          <History aria-hidden="true" />
          <div>
            <div className="state-title">A newer revision may exist</div>
            <FindingList pkg={asset.package} findings={possible} />
            <p className="muted">Check the two files. A shared document number alone does not make the entry stale.</p>
          </div>
        </div>
      )}
    </>
  );
}

function asField(f: RegisterField): Field {
  return { schema_version: "", ...f, alternatives: f.alternatives ?? [] };
}

export function AssetBody({ asset, onCite }: { asset: AssetView; onCite: (f: Field) => void }) {
  return (
    <>
      <RevisionBanner asset={asset} />

      <div className="tiles" style={{ marginBottom: 24 }}>
        <div className="card tile"><div className="l">Identity fields answered</div>
          <div className="n">{Math.round(asset.completeness * 100)}%</div></div>
        <div className="card tile"><div className="l">Needs review</div><div className="n">{asset.needs_review.length}</div></div>
        <div className="card tile"><div className="l">Register version</div><div className="n">{asset.version}</div></div>
      </div>

      <TableScroll label="Register fields" className="card" style={{ padding: 0, marginBottom: 24 }}>
        <table>
          <thead><tr><th>Field</th><th>Value</th><th>Confidence</th><th>Evidence</th><th>Source</th></tr></thead>
          <tbody>
            {asset.fields.length === 0 && <tr><td colSpan={5} className="muted">No fields recorded yet.</td></tr>}
            {asset.fields.map((f) => <FieldRow key={f.name} field={asField(f)} onCite={onCite} />)}
          </tbody>
        </table>
      </TableScroll>

      <div className="grid-2" style={{ gap: 24, marginBottom: 24 }}>
        <section>
          <h3 style={{ fontSize: 18, marginBottom: 12 }}><span className="row"><FolderKanban size={18} /> Open cases</span></h3>
          {asset.open_cases.length === 0 ? (
            <p className="muted">No open cases on this tag.</p>
          ) : (
            <ul className="stub-list">
              {asset.open_cases.map((id) => (
                <li key={id}><Link to={`/cases/${encodeURIComponent(id)}`} className="mono">Case {id.slice(0, 8)}</Link></li>
              ))}
            </ul>
          )}
        </section>
        <section>
          <h3 style={{ fontSize: 18, marginBottom: 12 }}><span className="row"><FileText size={18} /> Downloads</span></h3>
          <p className="muted" style={{ fontSize: 13, marginBottom: 8 }}>Every value carries its document and page.</p>
          <ExportButtons label={`Downloads for ${asset.tag}`} links={assetExports(asset.package, asset.tag)} />
          <div style={{ marginTop: 12 }}>
            <ExportButtons label={`Overview for ${asset.package}`} links={[overviewExport(asset.package)]} />
          </div>
        </section>
      </div>

      <h3 style={{ fontSize: 18, marginBottom: 12 }}>Documents read</h3>
      <TableScroll label="Documents read" className="card" style={{ padding: 0 }}>
        <table>
          <thead><tr><th>File</th><th>Type</th><th>Document number</th><th>Revision</th></tr></thead>
          <tbody>
            {asset.sources.length === 0 && <tr><td colSpan={4} className="muted">No documents recorded.</td></tr>}
            {asset.sources.map((s, i) => {
              const type = String(s.doc_type ?? "");
              return (
                <tr key={String(s.document_id ?? i)}>
                  <td className="mono" style={{ wordBreak: "break-all" }}>
                    <CitedFile pkg={asset.package} path={s.source_path as string | null | undefined} />
                  </td>
                  <td>{DOC_TYPE_LABELS[type] ?? (type.replace(/_/g, " ") || "not classified")}</td>
                  <td className="mono">{String(s.document_number ?? "") || <span className="muted">not read</span>}</td>
                  <td className="mono">{String(s.revision ?? "") || <span className="muted">not read</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableScroll>
    </>
  );
}

/** A read of the asset that failed. A 404 is not a fault: the tag has no register entry yet. */
export function AssetFailure({ pkg, tag, error, onRetry }: { pkg: string; tag: string; error: unknown; onRetry?: () => void }) {
  if (error instanceof ApiError && error.status === 404) {
    return (
      <Empty title={`No register entry for ${tag} in ${pkg} yet`}>
        The register holds a tag once a module run has recorded it. An extraction result on its own is not written here.
      </Empty>
    );
  }
  if (error instanceof ApiError && error.status === 403) {
    return (
      <div className="state-box" role="status">
        <Lock aria-hidden="true" />
        <div><div className="state-title">This package is outside your access</div><p className="muted">{error.message}</p></div>
      </div>
    );
  }
  return <ErrorState error={error} onRetry={onRetry} />;
}
